// @ts-nocheck
const LAST_ACTIVITY_KEY = 'last_user_activity_at';
const PROMPT_CLAIM_KEY = 'vault_session_prompt';
const DEFAULT_TIMEOUT_MS = 60 * 60 * 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 10 * 1000;
const EVENT_THROTTLE_MS = 500;
const CLAIM_STALE_MS = 3 * 60 * 1000;
const CLAIM_HEARTBEAT_MS = 30 * 1000;
const IDLE_GESTURE_WINDOW_MS = 10000;
const LOGOUT_OWNER_ID = 'logout';

const TAB_ID = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

let memoryActivityAt = 0;
let lastActivityWriteAt = 0;
let trackingStarted = false;
let claimHeartbeatId = null;
let idleGestureUntil = 0;

/**
 * The inactivity gate is opt-in. Any other value keeps the existing session flow.
 * @returns {boolean}
 */
export const isInactivityPromptEnabled = () =>
  window.CONFIG?.ENABLE_INACTIVITY_SESSION_PROMPT === true;

/**
 * @returns {number} Configured idle window, or one hour when the value is unusable.
 */
const getInactivityTimeoutMs = () => {
  const configured = Number(window.CONFIG?.INACTIVITY_PROMPT_TIMEOUT_MS);
  return Number.isFinite(configured) && configured > 0
    ? configured
    : DEFAULT_TIMEOUT_MS;
};

/**
 * @returns {number} Shared activity timestamp, or 0 when it has never been stored.
 */
const readStoredActivity = () => {
  try {
    const stored = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
    return Number.isFinite(stored) ? stored : 0;
  } catch (_error) {
    return memoryActivityAt;
  }
};

/**
 * @param {number} now - Activity time to publish.
 * @param {boolean} force - Write even inside the throttle window.
 * @returns {void}
 */
const writeActivity = (now, force) => {
  memoryActivityAt = Math.max(memoryActivityAt, now);
  if (!force && now - lastActivityWriteAt < ACTIVITY_WRITE_THROTTLE_MS) return;

  try {
    const stored = readStoredActivity();
    // A newer stamp from another tab is the shared clock. Do not move it backward.
    if (stored > now) {
      memoryActivityAt = stored;
      lastActivityWriteAt = now;
      return;
    }
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    lastActivityWriteAt = now;
  } catch (_error) {
    lastActivityWriteAt = now;
  }
};

/**
 * Idle means no user input in any tab for the configured window.
 * API traffic is not activity.
 * @returns {boolean}
 */
export const isUserInactive = () => {
  const latest = Math.max(memoryActivityAt, readStoredActivity());
  if (!latest) return true;
  return Date.now() - latest >= getInactivityTimeoutMs();
};

/**
 * A click after the idle period still starts the request that returns 401.
 * Remember that the user was idle long enough for that request to see it,
 * because the click itself updates the activity clock first.
 * @returns {void}
 */
const rememberGestureIdleState = () => {
  if (!isUserInactive()) return;
  idleGestureUntil = Date.now() + IDLE_GESTURE_WINDOW_MS;
};

/**
 * True when the user is idle now, or was idle just before the click that
 * started the current requests.
 * @returns {boolean}
 */
export const shouldPromptBecauseIdle = () =>
  Date.now() < idleGestureUntil || isUserInactive();

/**
 * Later requests after the continue popup should follow the new activity clock.
 * @returns {void}
 */
export const clearIdleReturn = () => {
  idleGestureUntil = 0;
};

/**
 * @returns {{ ownerId: string, at: number }|null}
 */
const readPromptClaim = () => {
  try {
    const raw = localStorage.getItem(PROMPT_CLAIM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ownerId || typeof parsed.at !== 'number') return null;
    return parsed;
  } catch (_error) {
    return null;
  }
};

/**
 * @param {{ ownerId: string, at: number }|null} claim - Stored popup owner.
 * @returns {boolean}
 */
const isClaimStale = (claim) => {
  if (!claim) return true;
  return Date.now() - claim.at >= CLAIM_STALE_MS;
};

/**
 * True when a different live tab, or a logout in progress, owns the continue popup.
 * @returns {boolean}
 */
export const hasActiveForeignClaim = () => {
  const claim = readPromptClaim();
  if (!claim || isClaimStale(claim)) return false;
  return claim.ownerId !== TAB_ID;
};

/**
 * Become the only tab allowed to open the continue popup.
 * A read-back resolves two tabs writing the claim at the same time.
 * @returns {boolean} True when this tab owns the claim.
 */
export const claimSessionPrompt = () => {
  const existing = readPromptClaim();
  if (existing?.ownerId === TAB_ID && !isClaimStale(existing)) return true;
  if (existing && existing.ownerId !== TAB_ID && !isClaimStale(existing)) {
    return false;
  }

  try {
    localStorage.setItem(
      PROMPT_CLAIM_KEY,
      JSON.stringify({ ownerId: TAB_ID, at: Date.now() })
    );
  } catch (_error) {
    // Without storage, this tab still has to be able to ask the user.
    return true;
  }

  return readPromptClaim()?.ownerId === TAB_ID;
};

/**
 * Drop the claim when this tab owns it so another open tab can take the popup.
 * @returns {void}
 */
export const releasePromptClaim = () => {
  if (readPromptClaim()?.ownerId !== TAB_ID) return;
  try {
    localStorage.removeItem(PROMPT_CLAIM_KEY);
  } catch (_error) {
    // The claim expires on its own if storage is unavailable.
  }
};

/**
 * Keep the claim reserved across logout so other tabs do not open a second popup
 * in the gap before storage is cleared and they reload.
 * @returns {void}
 */
export const holdPromptForLogout = () => {
  try {
    localStorage.setItem(
      PROMPT_CLAIM_KEY,
      JSON.stringify({ ownerId: LOGOUT_OWNER_ID, at: Date.now() })
    );
  } catch (_error) {
    // Logout still clears storage and broadcasts to the other tabs.
  }
};

/**
 * Refresh the claim timestamp while the dialog is open.
 * A crashed tab stops heartbeating and the claim becomes available after a few minutes.
 * @returns {void}
 */
export const touchPromptClaim = () => {
  if (readPromptClaim()?.ownerId !== TAB_ID) return;
  try {
    localStorage.setItem(
      PROMPT_CLAIM_KEY,
      JSON.stringify({ ownerId: TAB_ID, at: Date.now() })
    );
  } catch (_error) {
    // The original claim remains until it is treated as abandoned.
  }
};

/**
 * @returns {void}
 */
export const startPromptHeartbeat = () => {
  window.clearInterval(claimHeartbeatId);
  claimHeartbeatId = window.setInterval(touchPromptClaim, CLAIM_HEARTBEAT_MS);
};

/**
 * @returns {void}
 */
export const stopPromptHeartbeat = () => {
  window.clearInterval(claimHeartbeatId);
  claimHeartbeatId = null;
};

/**
 * Record real user input for this origin. No-ops unless the env flag is on,
 * so the existing session flow does not add listeners or storage writes.
 * @returns {void}
 */
export const startUserActivityTracking = () => {
  if (trackingStarted || !isInactivityPromptEnabled()) return;
  trackingStarted = true;
  writeActivity(Date.now(), true);

  let lastEventAt = 0;
  const handleEvent = () => {
    // Snapshot idle before this gesture updates the clock, including when a
    // second event in the same click is throttled.
    rememberGestureIdleState();
    const now = Date.now();
    if (now - lastEventAt < EVENT_THROTTLE_MS) return;
    lastEventAt = now;
    writeActivity(now, false);
  };

  const handlePageHide = () => {
    stopPromptHeartbeat();
    releasePromptClaim();
  };

  // Capture so a click still counts when a control stops the event from bubbling.
  // Pointer movement and scrolling do not count: moving onto a button was
  // marking the user active before that click's request was sent, so the
  // continue popup never opened after the idle period.
  window.addEventListener('pointerdown', handleEvent, true);
  window.addEventListener('mousedown', handleEvent, true);
  window.addEventListener('keydown', handleEvent, true);
  window.addEventListener('touchstart', handleEvent, true);
  window.addEventListener('click', handleEvent, true);
  window.addEventListener('pagehide', handlePageHide);
};
